
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Download, FileText, Clock, CheckCircle, XCircle } from 'lucide-react';
import { requestDataExport, getExportRequests, DataExportRequest } from '@/services/gdprDataExportService';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const DataExportPanel: React.FC = () => {
  const [requests, setRequests] = useState<DataExportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [format, setFormat] = useState<'json' | 'csv' | 'both'>('json');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const data = await getExportRequests();
      setRequests(data);
    } finally {
      setLoading(false);
    }
  };

  const handleExportRequest = async () => {
    setExporting(true);
    try {
      const result = await requestDataExport(format);
      if (result.success) {
        await loadRequests();
      }
    } finally {
      setExporting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-600" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      completed: 'default',
      failed: 'destructive',
      processing: 'secondary',
      pending: 'outline'
    };
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Download Your Data
        </CardTitle>
        <CardDescription>
          Export all your personal data in accordance with GDPR. Includes your profile, courses, progress, certificates, and activity logs.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Export Request Section */}
        <div className="p-4 border rounded-lg bg-muted/30">
          <h3 className="font-semibold mb-3">Request New Export</h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={format} onValueChange={(v: any) => setFormat(v)}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="json">JSON Format</SelectItem>
                <SelectItem value="csv">CSV Format</SelectItem>
                <SelectItem value="both">Both Formats</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={handleExportRequest}
              disabled={exporting}
              className="w-full sm:w-auto"
            >
              {exporting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Download className="h-4 w-4 mr-2" />
              Request Export
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            ℹ️ You can request one export every 24 hours. Files expire after 7 days.
          </p>
        </div>

        {/* Export History */}
        <div>
          <h3 className="font-semibold mb-3">Export History</h3>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No export requests yet
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((request) => (
                <div
                  key={request.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex items-start gap-3">
                    {getStatusIcon(request.status)}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">
                          {request.export_format.toUpperCase()} Export
                        </span>
                        {getStatusBadge(request.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Requested: {new Date(request.created_at).toLocaleString()}
                      </p>
                      {request.completed_at && (
                        <p className="text-sm text-muted-foreground">
                          Completed: {new Date(request.completed_at).toLocaleString()}
                        </p>
                      )}
                      {request.file_size && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Size: {(request.file_size / 1024).toFixed(2)} KB
                        </p>
                      )}
                    </div>
                  </div>
                  {request.status === 'completed' && request.file_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(request.file_url!, '_blank')}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Information Section */}
        <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-950/20">
          <h4 className="font-semibold text-sm mb-2">What's included in your export?</h4>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
            <li>Profile information (name, email, settings)</li>
            <li>All course assignments and progress</li>
            <li>Quiz attempts and scores</li>
            <li>Certificates earned</li>
            <li>Complete activity and learning history</li>
            <li>Preferences and account settings</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
